import React, { FC } from "react";
import { motion, Reorder } from "framer-motion";
import { get } from "lodash";
import styled from "styled-components";

import { primary } from "@formant/ui/component/common/Colors";

import { Label } from "./Label";
import { ListButton } from "./ListButton";
import { IInputProps, ServiceParameters } from "./types";
import { updatePath } from "./updatePath";

interface IJsonSchemaArrayOfObjectsProps extends IInputProps {}

export const JsonSchemaArrayOfObjects: FC<
    IJsonSchemaArrayOfObjectsProps
> = props => {
    const { params, setParams, path, setCurrentPath, schema } = props;
    const values = getArrayValue(params, path);

    return (
        <Container>
            <Label
                value={schema.title}
                description={schema.description}
                link={schema["$formant.documentationUrl"]}
            />
            <ListContainer>
                <Reorder.Group
                    axis="y"
                    values={values}
                    onReorder={(items: string[]) => {
                        setParams(prev => updatePath(prev, path, items));
                    }}
                    layoutScroll={true}
                >
                    {values.map((value, newIndex) => (
                        <Reorder.Item
                            key={[
                                ...path,
                                getFirstArrayFieldValue(value),
                                JSON.stringify(value),
                            ].join(".")}
                            drag="y"
                            value={value}
                            whileDrag={{ zIndex: 2, position: "relative" }}
                        >
                            <motion.div
                                onTap={() => {
                                    setCurrentPath([...path, String(newIndex)]);
                                }}
                            >
                                <DraggableListButton
                                    kebab={true}
                                    icon="chevron-right"
                                    index={newIndex}
                                >
                                    {getFirstArrayFieldValue(value) || (
                                        <i>unknown name</i>
                                    )}
                                </DraggableListButton>
                            </motion.div>
                        </Reorder.Item>
                    ))}
                </Reorder.Group>

                <AddListButton
                    items={values.length}
                    onClick={() => {
                        const nextArrayPath = [...path, String(values.length)];
                        setParams(prev => updatePath(prev, nextArrayPath, {}));
                        setCurrentPath(nextArrayPath);
                    }}
                    empty={true}
                >
                    Add...
                </AddListButton>
            </ListContainer>
        </Container>
    );
};

export const getArrayValue = (
    params: ServiceParameters,
    currentPath: string[],
): [] => {
    return get(params, currentPath) || [];
};

const firstArrayValue = (obj: Record<string, any>): string | undefined => {
    if (obj.name) {
        return obj.name;
    }

    if (obj.propertyName) {
        return obj.propertyName;
    }

    const values = Object.values(obj);
    if (values.length) {
        return values.find(_ => typeof _ === "string");
    }
    return undefined;
};

export const getFirstArrayFieldValue = (
    obj: Record<string, any>,
    placeholder?: string,
): string | undefined => {
    return firstArrayValue(obj) || placeholder;
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    z-index: 0;
`;

const AddListButton = styled(ListButton)<{ items: number }>`
    position: relative;
    z-index: 0;
    margin-bottom: 0rem !important;

    border-radius: ${props =>
        props.items > 0
            ? "0rem 0rem 0.1875rem 0.1875rem"
            : "0.1875rem 0.1875rem 0.1875rem 0.1875rem"};
`;

const DraggableListButton = styled(ListButton)<{ index: number }>`
    border-radius: ${props =>
        props.index === 0 ? " 0.1875rem 0.1875rem 0rem 0rem " : "0rem"};
`;

const ListContainer = styled(Container)`
    background-color: ${primary.steel01};
    width: 19.5rem;
    border-radius: 0.1875rem;

    ul {
        padding: 0rem;
        margin: 0rem;
        list-style: none;
    }
`;
